import { useCallback, useState } from 'react';
import { toast } from 'sonner';
import { getPresignedUrl } from '@/services/api/reports';
import { openInNewTab } from '@/utils/download';

/**
 * Downloads BOP bucket files through presigned URLs, tracking every in-flight key so two overlapping
 * downloads never clear each other's state.
 */
export const usePresignedDownload = () => {
  const [downloadingKeys, setDownloadingKeys] = useState<ReadonlySet<string>>(new Set());

  const download = useCallback(async (key: string) => {
    setDownloadingKeys((previous) => new Set([...previous, key]));
    try {
      const { url } = await getPresignedUrl(key);

      openInNewTab(url);
    } catch {
      toast.error('Failed to download file');
    } finally {
      setDownloadingKeys((previous) => new Set([...previous].filter((existing) => existing !== key)));
    }
  }, []);

  const isDownloading = useCallback((key: string) => downloadingKeys.has(key), [downloadingKeys]);

  return { download, isDownloading, downloadingKeys };
};
